import axios, { AxiosError, AxiosRequestHeaders, HttpStatusCode } from 'axios';
import { AxiosRequestCustomConfig } from './useFetch';
import { refreshJWT } from '@/repository/authen';
import { clearToken, getToken, setToken } from '@/util/localStorage';
import { redirectURL } from '@/util/redirect';
import axiosRetry from 'axios-retry';
import { config } from '@/config';

const mainBaseURL = config.host;
const backupBaseURL = config.backupHost;

const instance = axios.create({
	baseURL: mainBaseURL,
	timeout: 1000 * 60 * 10, // 10 minutes
	headers: {
		'Content-Type': 'application/json'
	}
});

instance.interceptors.request.use(
	(config) => {
		if (config.params) {
			for (const [key, value] of Object.entries(config.params)) {
				if (!value || (typeof value === 'string' && value.length === 0)) {
					config.params[key] = null;
				}
			}
		}

		const accessToken = getToken();
		if (accessToken?.accessToken) {
			if (!config.headers) {
				config.headers = { 'Content-Type': 'application/json' } as AxiosRequestHeaders;
			}
			config.headers.Authorization = `Bearer ${accessToken.accessToken}`;
		}

		return config;
	},
	(error) => Promise.reject(error)
);

instance.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		const config = { ...error.config } as AxiosRequestCustomConfig;
		if (error.response?.status === HttpStatusCode.Unauthorized) {
			try {
				if (config._isRefreshing) {
					clearToken();
					window.location.href = '/signin' + redirectURL();
					// alert session expired
					// redirect to signin page
					return Promise.reject(error);
				}
				config._isRefreshing = true;

				const token = getToken();
				if (!token || !token.refreshToken) {
					throw Error('No refresh token');
				}

				const jwt = await refreshJWT(token.refreshToken);
				if (!jwt) {
					throw Error('Refresh token is invalid');
				}

				setToken(jwt);

				if (!config.headers) {
					config.headers = { 'Content-Type': 'application/json' };
				}
				config.headers.Authorization = `Bearer ${jwt.accessToken}`;

				return instance.request(config);
			} catch (error) {
				clearToken();
				window.location.href = '/signin' + redirectURL();
				// alert session expired
				// redirect to signin page
				return Promise.reject(error);
			}
		} else if (error.code === 'ERR_NETWORK' && !config._isRetry && mainBaseURL !== backupBaseURL) {
			config._isRetry = true;
			config.baseURL = backupBaseURL;
			return instance.request(config);
		}
		return Promise.reject(error);
	}
);

axiosRetry(instance, {
	retries: 5,
	retryCondition(error) {
		if (error.response?.status) {
			return error.response?.status >= 500;
		}
		return false;
	}
});

export default instance;
